import React from 'react';
import { motion } from 'framer-motion';
import { 
  IoLogoJavascript, 
  IoLogoReact, 
  IoLogoNodejs, 
  IoLogoDocker,
  IoLogoAmazon,
  IoLogoGithub,
  IoDownload,
  IoSchool
} from 'react-icons/io5';

const Resume = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const technologies = [
    { name: 'JavaScript', icon: IoLogoJavascript, color: 'text-yellow-400' },
    { name: 'React', icon: IoLogoReact, color: 'text-blue-400' },
    { name: 'Node.js', icon: IoLogoNodejs, color: 'text-green-400' },
    { name: 'Docker', icon: IoLogoDocker, color: 'text-blue-500' },
    { name: 'AWS', icon: IoLogoAmazon, color: 'text-orange-400' },
    { name: 'Git', icon: IoLogoGithub, color: 'text-gray-400' },
  ];

  const additionalSkills = [
    'TypeScript', 'Python', 'MongoDB', 'GitHub Actions', 'Tailwind CSS', 'Next.js',
    'Bootstrap', 'Figma', 'Postman', 'Linux', 'VS Code', 'Git'
  ];

  const education = [
    {
      degree: 'Bachelor of Science in Computer Science',
      institution: 'National University of Computer and Emerging Sciences',
      period: 'Class of 2023',
      description: 'Focused on software engineering, algorithms, and data structures. Completed comprehensive coursework in computer science fundamentals.',
      achievements: ['Software Engineering', 'Data Structures & Algorithms']
    },
    {
      degree: 'Intermediate in Computer Science',
      institution: 'KIPS College Lahore, Pakistan',
      period: 'Class of 2021',
      description: 'Foundation in computer science principles and mathematics.',
      achievements: ['Computer Science Foundation', 'Mathematics', 'Physics']
    }
  ];

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className="max-w-6xl"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white-1 mb-6">
              My <span className="gradient-text">Resume</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola rounded-full"></div>
          </div>
          
          <motion.a
            href="/resume.pdf"
            download="Rayyan_Shaheer_Resume.pdf"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-accent-yellow to-orange-yellow-crayola text-bg-dark font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-yellow/25 transition-all duration-300"
          >
            <IoDownload size={20} />
            Download Resume
          </motion.a>
        </div>
      </motion.div>

      {/* Skills Section */}
      <motion.div variants={itemVariants} className="mb-16">
        <h3 className="text-lg font-bold text-white-1 mb-8">
          Technical <span className="gradient-text">Skills</span>
        </h3>
        
        {/* Main Technologies */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          {technologies.map((tech, index) => (
            <motion.div
              key={tech.name}
              variants={itemVariants}
              whileHover={{ scale: 1.1, y: -5 }}
              className="group bg-card-dark/50 backdrop-blur-sm border border-jet/30 rounded-xl p-6 text-center hover:border-accent-yellow/30 transition-all duration-300 glow-hover"
            >
              <tech.icon 
                size={48} 
                className={`${tech.color} mx-auto mb-3 group-hover:scale-110 transition-transform duration-300`} 
              />
              <h4 className="text-white-1 font-medium text-sm">{tech.name}</h4>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <div className="bg-card-dark/30 border border-jet/30 rounded-xl p-6 backdrop-blur-sm">
          <h4 className="text-lg font-semibold text-white-1 mb-4">Additional Technologies</h4>
          <div className="flex flex-wrap gap-3">
            {additionalSkills.map((skill, index) => (
              <motion.span
                key={skill}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="px-4 py-2 bg-jet/50 text-accent-yellow rounded-full text-sm border border-accent-yellow/20 hover:bg-accent-yellow/10 transition-colors duration-300"
              >
                {skill}
              </motion.span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Education */}
      <motion.div variants={itemVariants}>
        <h3 className="text-lg font-bold text-white-1 mb-8 flex items-center gap-3">
          <IoSchool className="text-accent-yellow" />
          Education
        </h3>
          
          <div className="space-y-8">
            {education.map((edu, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative pl-8 pb-8 border-l-2 border-accent-yellow/30 last:border-l-0 last:pb-0"
              >
                {/* Timeline dot */}
                <div className="absolute -left-2 top-0 w-4 h-4 bg-accent-yellow rounded-full border-4 border-bg-dark"></div>
                
                <div className="bg-card-dark/50 backdrop-blur-sm border border-jet/30 rounded-xl p-6 hover:border-accent-yellow/30 transition-all duration-300">
                  <h4 className="text-lg font-semibold text-white-1 mb-2">{edu.degree}</h4>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                    <p className="text-accent-yellow font-medium">{edu.institution}</p>
                    <span className="text-light-gray text-sm">{edu.period}</span>
                  </div>
                  <p className="text-light-gray mb-4">{edu.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {edu.achievements.map((achievement, achIndex) => (
                      <span
                        key={achIndex}
                        className="px-3 py-1 text-xs bg-accent-yellow/10 text-accent-yellow rounded-full border border-accent-yellow/20"
                      >
                        {achievement}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
    </motion.div>
  );
};

export default Resume;
